// frontend/components/HrManagerWorkScheduleTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchEmployeesList,
  fetchWorkSchedule,
  updateWorkSchedule,
} from "../features/hr/hrThunks.js";
import { Clock, Users } from "lucide-react";
import "../styles/hr/hr-manager-work-schedule.css";

const daysConfig = [
  { key: "monday", label: "Lunes" },
  { key: "tuesday", label: "Martes" },
  { key: "wednesday", label: "Miércoles" },
  { key: "thursday", label: "Jueves" },
  { key: "friday", label: "Viernes" },
  { key: "saturday", label: "Sábado" },
  { key: "sunday", label: "Domingo" },
];

const createEmptySchedule = () => {
  const base = {};
  daysConfig.forEach((day) => {
    base[`${day.key}_start`] = "";
    base[`${day.key}_end`] = "";
  });
  return base;
};

const normalizeTimeField = (value) => {
  if (!value) return "";
  return String(value).slice(0, 5); // "09:00:00" -> "09:00"
};

const HrManagerWorkScheduleTab = () => {
  const dispatch = useDispatch();

  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [scheduleForm, setScheduleForm] = useState(() => createEmptySchedule());
  const [successMessage, setSuccessMessage] = useState("");

  // Empleados
  const employeesListRaw = useSelector((state) => state.hr.employeesList) || [];
  const loadingEmployeesList = useSelector((state) => state.hr.loadingEmployeesList);
  const errorEmployeesList = useSelector((state) => state.hr.errorEmployeesList);

  const employeesList = useMemo(() => {
    if (Array.isArray(employeesListRaw)) return employeesListRaw;
    if (employeesListRaw && Array.isArray(employeesListRaw.results)) return employeesListRaw.results;
    return [];
  }, [employeesListRaw]);

  // Horarios
  const workScheduleByEmployee = useSelector((state) => state.hr.workScheduleByEmployee) || {};
  const loadingWorkSchedule = useSelector((state) => state.hr.loadingWorkSchedule);
  const errorWorkSchedule = useSelector((state) => state.hr.errorWorkSchedule);
  const loadingUpdateWorkSchedule = useSelector((state) => state.hr.loadingUpdateWorkSchedule);
  const errorUpdateWorkSchedule = useSelector((state) => state.hr.errorUpdateWorkSchedule);

  const selectedEmployee =
    employeesList.find((employee) => String(employee.id) === String(selectedEmployeeId)) || null;

  const scheduleFromStore = selectedEmployeeId
    ? workScheduleByEmployee[selectedEmployeeId]
    : null;

  // Cargar empleados al entrar
  useEffect(() => {
    if (!employeesList || employeesList.length === 0) {
      dispatch(fetchEmployeesList());
    }
  }, [dispatch, employeesList]);

  // Seleccionar por defecto el primero
  useEffect(() => {
    if (!selectedEmployeeId && employeesList.length > 0) {
      setSelectedEmployeeId(String(employeesList[0].id));
    }
  }, [employeesList, selectedEmployeeId]);

  // Cambia empleado -> pedir horario
  useEffect(() => {
    setSuccessMessage("");
    if (!selectedEmployeeId) {
      setScheduleForm(createEmptySchedule());
      return;
    }
    dispatch(fetchWorkSchedule({ employeeId: selectedEmployeeId }));
  }, [dispatch, selectedEmployeeId]);

  // Sincronizar store -> form
  useEffect(() => {
    if (!selectedEmployeeId) {
      setScheduleForm(createEmptySchedule());
      return;
    }

    if (scheduleFromStore) {
      const next = createEmptySchedule();
      daysConfig.forEach((day) => {
        const startKey = `${day.key}_start`;
        const endKey = `${day.key}_end`;
        next[startKey] = normalizeTimeField(scheduleFromStore[startKey]);
        next[endKey] = normalizeTimeField(scheduleFromStore[endKey]);
      });
      setScheduleForm(next);
      return;
    }

    setScheduleForm(createEmptySchedule());
  }, [selectedEmployeeId, scheduleFromStore]);

  const handleTimeChange = (dayKey, field, value) => {
    setSuccessMessage("");
    setScheduleForm((prev) => ({
      ...prev,
      [`${dayKey}_${field}`]: value,
    }));
  };

  const handleClearDay = (dayKey) => {
    setSuccessMessage("");
    setScheduleForm((prev) => ({
      ...prev,
      [`${dayKey}_start`]: "",
      [`${dayKey}_end`]: "",
    }));
  };

  const handleCopyMondayToWeek = () => {
    setSuccessMessage("");
    const mondayStart = scheduleForm["monday_start"];
    const mondayEnd = scheduleForm["monday_end"];
    if (!mondayStart && !mondayEnd) return;

    setScheduleForm((prev) => {
      const next = { ...prev };
      daysConfig.forEach((day) => {
        if (day.key === "monday") return;
        next[`${day.key}_start`] = mondayStart;
        next[`${day.key}_end`] = mondayEnd;
      });
      return next;
    });
  };

  const handleSave = () => {
    if (!selectedEmployeeId) return;
    setSuccessMessage("");

    const payload = {};
    daysConfig.forEach((day) => {
      const startKey = `${day.key}_start`;
      const endKey = `${day.key}_end`;
      payload[startKey] = scheduleForm[startKey] || null;
      payload[endKey] = scheduleForm[endKey] || null;
    });

    dispatch(
      updateWorkSchedule({
        employeeId: selectedEmployeeId,
        scheduleData: payload,
      })
    ).then((action) => {
      if (action.type.endsWith("fulfilled")) {
        setSuccessMessage("Horario guardado correctamente.");
      }
    });
  };

  const canSave =
    !!selectedEmployeeId && !loadingWorkSchedule && !loadingUpdateWorkSchedule;

  const employeeLabel = selectedEmployee
    ? (
        (selectedEmployee.user?.first_name || "") +
        " " +
        (selectedEmployee.user?.last_name || "")
      ).trim() ||
      selectedEmployee.user?.username ||
      "Sin nombre"
    : "Selecciona un empleado";

  const { daysOffCount, daysWithScheduleCount } = useMemo(() => {
    let off = 0;
    let withSchedule = 0;

    daysConfig.forEach((day) => {
      const startValue = scheduleForm[`${day.key}_start`] || "";
      const endValue = scheduleForm[`${day.key}_end`] || "";
      const isOff = !startValue && !endValue;
      if (isOff) off += 1;
      else withSchedule += 1;
    });

    return { daysOffCount: off, daysWithScheduleCount: withSchedule };
  }, [scheduleForm]);

  return (
    <div className="hr-manager-tab hr-manager-work-schedule-tab">
      <div className="hr-manager-work-schedule-header">
        <div>
          <h2>Horarios base</h2>
          <p className="hr-tab-helper-text">
            Define o ajusta el horario semanal de cada empleado. Deja un día en
            blanco para marcarlo como libre.
          </p>
        </div>
      </div>

      {/* Selector + acciones */}
      <div className="hr-manager-work-schedule-controls">
        <div className="hr-manager-work-schedule-row">
          <div className="hr-filter-select">
            <label>Empleado</label>
            <select
              value={selectedEmployeeId}
              onChange={(event) => setSelectedEmployeeId(event.target.value)}
              disabled={loadingEmployeesList}
            >
              <option value="">
                {loadingEmployeesList ? "Cargando empleados..." : "Seleccionar empleado"}
              </option>
              {employeesList.map((employee) => {
                const user = employee.user || {};
                const fullName =
                  ((user.first_name || "") + " " + (user.last_name || "")).trim() ||
                  user.username ||
                  "Sin nombre";
                return (
                  <option key={employee.id} value={employee.id}>
                    {fullName}
                  </option>
                );
              })}
            </select>
          </div>

          <button
            type="button"
            className="hr-button hr-button--ghost"
            onClick={handleCopyMondayToWeek}
            disabled={!selectedEmployeeId}
            title="Copia el horario del lunes a martes-domingo"
          >
            <Users className="hr-button-icon" aria-hidden="true" />
            Copiar lunes
          </button>

          <button
            type="button"
            className="hr-button hr-button--primary"
            onClick={handleSave}
            disabled={!canSave}
          >
            <Clock className="hr-button-icon" aria-hidden="true" />
            {loadingUpdateWorkSchedule ? "Guardando..." : "Guardar horario"}
          </button>
        </div>

        {(errorEmployeesList || errorWorkSchedule || errorUpdateWorkSchedule) && (
          <div className="hr-error-banner">
            {errorEmployeesList && (
              <span>Error al cargar empleados: {errorEmployeesList}</span>
            )}
            {errorWorkSchedule && (
              <span>Error al cargar horario: {errorWorkSchedule}</span>
            )}
            {errorUpdateWorkSchedule && (
              <span>Error al guardar horario: {errorUpdateWorkSchedule}</span>
            )}
          </div>
        )}

        {successMessage && <div className="hr-success-banner">{successMessage}</div>}
      </div>

      {/* Métricas */}
      <div className="hr-manager-work-schedule-summary">
        <div className="hr-metric-card hr-metric-card--primary">
          <div className="hr-metric-top">
            <span className="hr-metric-label">Empleado</span>
            <Users className="hr-metric-icon" aria-hidden="true" />
          </div>
          <div className="hr-metric-value">{employeeLabel}</div>
          <p className="hr-metric-helper">Horario semanal base asignado a este empleado.</p>
        </div>

        <div className="hr-metric-card">
          <div className="hr-metric-top">
            <span className="hr-metric-label">Semana</span>
            <Clock className="hr-metric-icon" aria-hidden="true" />
          </div>
          <div className="hr-metric-value">
            {daysWithScheduleCount}
            <span className="hr-metric-unit">días con horario</span>
          </div>
          <p className="hr-metric-helper">
            {daysOffCount} día{daysOffCount === 1 ? "" : "s"} marcado{daysOffCount === 1 ? "" : "s"} como libre.
          </p>
        </div>
      </div>

      {/* Tabla */}
      <div className="hr-manager-work-schedule-table-card">
        <div className="hr-manager-work-schedule-table-header">
          <h3 className="hr-manager-work-schedule-title">Horario semanal base</h3>
          <div className="hr-manager-work-schedule-pill">
            <Clock className="hr-manager-work-schedule-pill-icon" aria-hidden="true" />
            <span>Edita por día y guarda</span>
          </div>
        </div>

        {!selectedEmployeeId ? (
          <div className="hr-manager-work-schedule-empty">
            Selecciona un empleado para definir su horario.
          </div>
        ) : loadingWorkSchedule && !scheduleFromStore ? (
          <div className="hr-manager-work-schedule-empty">Cargando horario...</div>
        ) : (
          <div className="hr-time-history-table-wrapper">
            <table className="hr-table hr-work-schedule-table">
              <thead>
                <tr>
                  <th>Día</th>
                  <th>Inicio</th>
                  <th>Fin</th>
                  <th style={{ textAlign: "right" }}>Día libre</th>
                </tr>
              </thead>
              <tbody>
                {daysConfig.map((day) => {
                  const startKey = `${day.key}_start`;
                  const endKey = `${day.key}_end`;
                  const startValue = scheduleForm[startKey] || "";
                  const endValue = scheduleForm[endKey] || "";
                  const isDayOff = !startValue && !endValue;

                  return (
                    <tr key={day.key}>
                      <td className="hr-work-schedule-day">
                        <span className="hr-work-schedule-day-name">{day.label}</span>
                        {isDayOff && <span className="hr-badge hr-badge--neutral">Libre</span>}
                      </td>
                      <td>
                        <input
                          type="time"
                          value={startValue}
                          onChange={(event) => handleTimeChange(day.key, "start", event.target.value)}
                          className="hr-time-input"
                        />
                      </td>
                      <td>
                        <input
                          type="time"
                          value={endValue}
                          onChange={(event) => handleTimeChange(day.key, "end", event.target.value)}
                          className="hr-time-input"
                        />
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <button
                          type="button"
                          className={`hr-button hr-button--small ${
                            isDayOff ? "hr-button--ghost" : "hr-button--danger-outline"
                          }`}
                          onClick={() => handleClearDay(day.key)}
                          disabled={isDayOff}
                          title={isDayOff ? "Este día ya está marcado como libre" : "Dejar el día sin horario"}
                        >
                          {isDayOff ? "Libre" : "Marcar libre"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <p className="hr-manager-work-schedule-helper">
              Consejo: define el horario del <strong>lunes</strong> y usa “Copiar lunes” para rellenar el resto de la semana.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HrManagerWorkScheduleTab;